import { DeclineCurveAnalysis } from 'components/well-page-content/decline-curve-analysis/decline-curve-analysis';
import type { DcaSaveAction } from 'components/well-page-content/decline-curve-analysis/dca-save-action';
import {
  dcaScenariosAtom,
  selectedDcaScenarioAtom,
  wellboresMutationAtom,
} from 'components/well-page-content/atoms';
import {
  dcaHotkeysHelpOpenAtom,
  dcaHotkeysModeAtom,
} from 'components/well-page-content/decline-curve-analysis/hotkeys-mode/atoms';
import styles from './dca-modal.module.scss';
import {
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  Help,
} from '@carbon/react/icons';
import { ActionableNotification, Button, Tag } from '@carbon/react';
import { ReactNode, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useProductionDataSources } from './hooks';

const WELLBORES_HREF = '/queries/wellbores';

interface DcaModalProps {
  uwi: string | null;
  wellName?: string | null;
  dcaForecastId?: string | null;
  /** Omitted by the Well page, which has no workspace to save into. */
  dcaWorkspaceId?: number;
  open: boolean;
  onClose: () => void;
  onForecastSaved?: () => void;
  /**
   * Position of this well in the wells table's display order, 1-based, and how
   * many wells that order holds. Shown next to the navigation arrows.
   */
  wellPosition?: { index: number; total: number };
  onPreviousWell?: () => void;
  onNextWell?: () => void;
  /** Takes over the configuration panel's Save button. */
  saveAction?: DcaSaveAction;
  /** Rendered above the editor. The Well page shows its save result here. */
  banner?: ReactNode;
}

export const DcaModal = ({
  uwi = null,
  wellName,
  dcaForecastId,
  dcaWorkspaceId,
  open,
  onClose,
  onForecastSaved,
  wellPosition,
  onPreviousWell,
  onNextWell,
  saveAction,
  banner,
}: DcaModalProps) => {
  const setWellboresData = useSetAtom(wellboresMutationAtom);
  const hotkeysMode = useAtomValue(dcaHotkeysModeAtom);
  const setHelpOpen = useSetAtom(dcaHotkeysHelpOpenAtom);
  const dcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const dcaScenarios = useAtomValue(dcaScenariosAtom);
  // The default source comes from the scenario, so wait while one is still
  // being chosen. Once the list has loaded without one, there is none.
  const scenariosLoaded = !dcaScenarios.loading && !!dcaScenarios.result;
  const dcaScenarioId = dcaScenario?.id ?? (scenariosLoaded ? null : undefined);
  const {
    sources: productionDataSources,
    loadFailed: productionSourcesFailed,
    retry: retryProductionSources,
    isFetching: retryingProductionSources,
  } = useProductionDataSources(open && uwi ? uwi : '', dcaScenarioId);

  useEffect(() => {
    if (!open || !uwi) return;
    setWellboresData({
      url: WELLBORES_HREF,
      type: 'POST',
      data: { uwi: [uwi] },
    });
  }, [open, uwi, setWellboresData]);

  if (!open || !uwi) return null;
  return (
    <div className={styles.dcaModal}>
      <div className={styles.dcaModalBackdrop} onClick={onClose} />
      <div className={styles.dcaModalContent}>
        <div className={styles.dcaModalHeader}>
          <div className={styles.dcaModalNav}>
            {/* The tearsheet body scrolls, so it clips anything that reaches
                past its padding. Both buttons sit at the left edge, and a
                centered tooltip is wider than the button, so it aligns to the
                button's left edge and opens rightwards instead. */}
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={ChevronLeft}
              iconDescription="Previous well"
              tooltipPosition="bottom"
              tooltipAlignment="start"
              disabled={!onPreviousWell}
              onClick={onPreviousWell}
            />
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={ChevronRight}
              iconDescription="Next well"
              tooltipPosition="bottom"
              tooltipAlignment="start"
              disabled={!onNextWell}
              onClick={onNextWell}
            />
            <h2 className={styles.dcaModalTitle}>
              Decline Curve Analysis
              <span className={styles.dcaModalWell}>
                {wellName ? `${uwi} - ${wellName}` : uwi}
                {wellPosition
                  ? ` (${wellPosition.index} of ${wellPosition.total})`
                  : ''}
              </span>
            </h2>
          </div>
          <div className={styles.dcaModalHotkeys}>
            {hotkeysMode.active && (
              <Tag type="blue" size="sm">
                {`Hotkeys: ${
                  hotkeysMode.mapping === 'aries' ? 'ARIES' : 'Insights'
                }`}
              </Tag>
            )}
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Help}
              iconDescription="Hotkeys help"
              tooltipPosition="bottom"
              tooltipAlignment="end"
              onClick={() => setHelpOpen(true)}
            />
          </div>
          <button className={styles.dcaModalCloseButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {/* Keyed by well so the arrows give the editor a clean mount. Its
            per-well responses and form state are held per instance, and
            carrying them across a well change renders the previous well's
            numbers against the new one. */}
        {banner}
        {productionSourcesFailed && (
          <ActionableNotification
            inline
            kind="warning"
            lowContrast
            hideCloseButton
            hasFocus={false}
            title="Couldn't load this well's production sources"
            subtitle="Showing IHS production."
            actionButtonLabel={
              retryingProductionSources ? 'Retrying...' : 'Retry'
            }
            onActionButtonClick={() => {
              if (!retryingProductionSources) retryProductionSources();
            }}
          />
        )}
        <DeclineCurveAnalysis
          key={uwi}
          uwi={uwi}
          wellName={wellName ?? undefined}
          dcaForecastId={dcaForecastId}
          dcaWorkspaceId={dcaWorkspaceId}
          insightsPdpForecastVersionsHref="/queries/reservoir/bulk_well_dca_forecasts/versions"
          legacyPdpForecastVersionsHref="/queries/reservoir/pdp_forecasts/versions"
          recordHref="/queries/reservoir/production_volumes"
          recordVersionHref="/queries/reservoir/production_volumes/versions"
          dailyRecordHref="/queries/reservoir/daily_production"
          dailyRecordVersionHref="/queries/reservoir/daily_production/versions"
          productionDataSources={productionDataSources}
          enableHotkeysMode
          onPreviousWell={onPreviousWell}
          onNextWell={onNextWell}
          onForecastSaved={onForecastSaved}
          saveAction={saveAction}
        />
      </div>
    </div>
  );
};
